import { useState } from 'react';
import type {
  GetPropertyUploadJobResponse,
  ListPropertyUploadJobsResponse,
  PropertyUploadJob,
  UploadPropertyCSVResponse,
  ValidatePropertyCSVResponse,
} from '@metron/shared';
import { getApiUrl } from '../config/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { toErrorMessage } from '@metron/shared';

export function usePropertyUploads() {
  const { getAccessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Validate CSV content (dry-run)
   */
  const validateCSV = async (csvContent: string, customerID: number) => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(getApiUrl('property-uploads/validate'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ csvContent, customerID }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to validate CSV');
      }

      const data: ValidatePropertyCSVResponse = await response.json();
      return data.validation;
    } catch (err) {
      const errorMessage = toErrorMessage(err, 'Failed to validate CSV');
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Upload CSV file and start processing
   */
  const uploadCSV = async (file: File, customerID: number) => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('customerID', customerID.toString());

      const response = await fetch(getApiUrl('property-uploads'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload CSV');
      }

      const data: UploadPropertyCSVResponse = await response.json();
      return data;
    } catch (err) {
      const errorMessage = toErrorMessage(err, 'Failed to upload CSV');
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get job status
   */
  const getJobStatus = async (jobId: string): Promise<PropertyUploadJob> => {
    const token = await getAccessToken();
    if (!token) {
      throw new Error('No access token available');
    }

    const response = await fetch(getApiUrl(`property-uploads/${jobId}`), {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to get job status');
    }

    const data: GetPropertyUploadJobResponse = await response.json();
    return data.job;
  };

  /**
   * List upload history
   */
  const listUploadHistory = async (allUsers = false): Promise<PropertyUploadJob[]> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const url = allUsers
        ? getApiUrl('property-uploads?allUsers=true')
        : getApiUrl('property-uploads');

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch upload history');
      }

      const data: ListPropertyUploadJobsResponse = await response.json();
      return data.jobs;
    } catch (err) {
      const errorMessage = toErrorMessage(err, 'Failed to fetch upload history');
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Download CSV template
   */
  const downloadTemplate = async () => {
    const token = await getAccessToken();
    if (!token) {
      throw new Error('No access token available');
    }

    try {
      // Fetch template using proper authentication headers (not query params)
      const response = await fetch(getApiUrl('property-uploads/template'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      // Get the CSV content as blob
      const blob = await response.blob();

      // Create a temporary download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'property-upload-template.csv';
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      const errorMessage = toErrorMessage(err, 'Failed to download template');
      setError(errorMessage);
      throw err;
    }
  };

  return {
    validateCSV,
    uploadCSV,
    getJobStatus,
    listUploadHistory,
    downloadTemplate,
    isLoading,
    error,
  };
}
